import React, { useState, useEffect } from 'react';
import {
  collection, addDoc, getDocs, deleteDoc, doc,
  query, orderBy, setDoc,
} from 'firebase/firestore';
import { db, CAMPAIGN_ID } from '../../firebase';
import { useMap } from '../../contexts/MapContext';
import { useCampaign } from '../../contexts/CampaignContext';
import { encodeTerrain, encodeFog, encodeElevation, decodeTerrain, decodeFog, decodeElevation } from '../../lib/mapCodec';

const ENCOUNTERS_COL = `campaigns/${CAMPAIGN_ID}/encounterTemplates`;

// Token fields we save (excludes player-specific things like ownerId)
const SAVE_FIELDS = [
  'name', 'type', 'x', 'y', 'hp', 'maxHp', 'size', 'speed',
  'initiativeBonus', 'ac', 'cr', 'colour', 'objectType', 'rotation',
  'conditions', 'buffs',
];

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  const hrs  = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 2)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24)  return `${hrs}h ago`;
  return `${days}d ago`;
}

export default function EncounterLibrary() {
  const { terrain, fog, elevation, loadCells } = useMap();
  const { tokens, spawnToken, removeToken, moveToken } = useCampaign();

  const [open,       setOpen]       = useState(false);
  const [name,       setName]       = useState('');
  const [saving,     setSaving]     = useState(false);
  const [loading,    setLoading]    = useState(null);
  const [deleting,   setDeleting]   = useState(null);
  const [encounters, setEncounters] = useState([]);
  const [fetching,   setFetching]   = useState(false);
  const [confirmLoad, setConfirmLoad] = useState(null);
  const [includeMap,  setIncludeMap]  = useState(true);

  useEffect(() => {
    if (!open) return;
    fetchEncounters();
  }, [open]);

  const fetchEncounters = async () => {
    setFetching(true);
    try {
      const q    = query(collection(db, ENCOUNTERS_COL), orderBy('savedAt', 'desc'));
      const snap = await getDocs(q);
      setEncounters(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } finally {
      setFetching(false);
    }
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      // Capture non-player tokens
      const tokenSnapshot = Object.values(tokens)
        .filter((t) => t.type !== 'player')
        .map((t) => {
          const saved = {};
          SAVE_FIELDS.forEach((f) => { if (t[f] !== undefined) saved[f] = t[f]; });
          return saved;
        });

      const data = {
        name: trimmed,
        savedAt: Date.now(),
        tokenCount: tokenSnapshot.length,
        tokens: tokenSnapshot,
        terrain:   includeMap ? encodeTerrain(terrain) : null,
        fog:       includeMap ? encodeFog(fog) : null,
        elevation: includeMap ? encodeElevation(elevation) : null,
      };

      // Overwrite if same name exists
      const existing = encounters.find(
        (e) => e.name.toLowerCase() === trimmed.toLowerCase()
      );
      if (existing) {
        await setDoc(doc(db, ENCOUNTERS_COL, existing.id), data);
      } else {
        await addDoc(collection(db, ENCOUNTERS_COL), data);
      }

      setName('');
      await fetchEncounters();
    } finally {
      setSaving(false);
    }
  };

  const handleLoad = async (encounter) => {
    setLoading(encounter.id);
    setConfirmLoad(null);
    try {
      // 1. Remove all existing non-player tokens
      const toRemove = Object.values(tokens).filter((t) => t.type !== 'player');
      await Promise.all(toRemove.map((t) => removeToken(t.id)));

      // 2. Load map terrain if saved with one
      if (encounter.terrain) {
        // New packed format — build legacy cells array for loadCells
        const gW = 30, gH = 30; // default grid size
        const len = gW * gH;
        const t = decodeTerrain(encounter.terrain, len);
        const f = encounter.fog ? decodeFog(encounter.fog, len) : new Array(len).fill(false);
        const e = encounter.elevation ? decodeElevation(encounter.elevation, len) : new Array(len).fill(0);
        const cells = t.map((tid, i) => ({ terrain: tid, fogRevealed: f[i], elevation: e[i] }));
        await loadCells(cells);
      } else if (encounter.cells && Array.isArray(encounter.cells)) {
        await loadCells(encounter.cells);
      }

      // 3. Respawn saved tokens at their saved positions
      for (const t of (encounter.tokens || [])) {
        const newId = await spawnToken({
          name:            t.name,
          type:            t.type || 'monster',
          colour:          t.colour,
          size:            t.size || 1,
          hp:              t.hp,
          maxHp:           t.maxHp,
          initiativeBonus: t.initiativeBonus || 0,
          ac:              t.ac,
          speed:           t.speed || 30,
          cr:              t.cr,
          objectType:      t.objectType,
          rotation:        t.rotation || 0,
        });
        // Move to saved grid position
        if (newId && (t.x !== undefined) && (t.y !== undefined)) {
          await moveToken(newId, t.x, t.y);
        }
      }
    } finally {
      setLoading(null);
    }
  };

  const handleDelete = async (encounter) => {
    setDeleting(encounter.id);
    try {
      await deleteDoc(doc(db, ENCOUNTERS_COL, encounter.id));
      setEncounters((prev) => prev.filter((e) => e.id !== encounter.id));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div style={styles.wrapper}>
      <button style={styles.toggle} onClick={() => setOpen((v) => !v)}>
        <span>⚔ Encounter Templates</span>
        <span style={styles.chevron}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={styles.body}>
          {/* Save current setup */}
          <div style={styles.saveRow}>
            <input
              style={styles.nameInput}
              placeholder="Encounter name…"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            />
            <button style={styles.saveBtn} onClick={handleSave} disabled={!name.trim() || saving}>
              {saving ? '…' : '💾 Save'}
            </button>
          </div>

          <label style={styles.checkRow}>
            <input
              type="checkbox"
              checked={includeMap}
              onChange={(e) => setIncludeMap(e.target.checked)}
              style={{ marginRight: 6 }}
            />
            <span style={styles.checkLabel}>Include map terrain</span>
          </label>

          <div style={styles.saveNote}>
            Saves all monsters &amp; objects on the map (not player tokens).
          </div>

          {/* Saved list */}
          {fetching && <div style={styles.hint}>Loading…</div>}
          {!fetching && encounters.length === 0 && (
            <div style={styles.hint}>No encounter templates yet.</div>
          )}

          {encounters.map((enc) => {
            const isConfirming = confirmLoad === enc.id;
            return (
              <div key={enc.id} style={styles.entry}>
                <div style={styles.entryInfo}>
                  <span style={styles.entryName}>{enc.name}</span>
                  <span style={styles.entryMeta}>
                    {enc.tokenCount ?? 0} tokens
                    {(enc.terrain || enc.cells) ? ' · with map' : ' · no map'}
                    {' · '}{timeAgo(enc.savedAt)}
                  </span>
                </div>

                {isConfirming ? (
                  <div style={styles.confirmRow}>
                    <span style={styles.confirmText}>
                      Clear monsters{(enc.terrain || enc.cells) ? ' & load map' : ''}?
                    </span>
                    <button style={styles.confirmYes} onClick={() => handleLoad(enc)}>
                      {loading === enc.id ? '…' : 'Yes'}
                    </button>
                    <button style={styles.confirmNo} onClick={() => setConfirmLoad(null)}>No</button>
                  </div>
                ) : (
                  <div style={styles.entryActions}>
                    <button
                      style={styles.loadBtn}
                      onClick={() => setConfirmLoad(enc.id)}
                      disabled={!!loading}
                    >
                      Load
                    </button>
                    <button
                      style={styles.deleteBtn}
                      onClick={() => handleDelete(enc)}
                      disabled={deleting === enc.id}
                    >
                      {deleting === enc.id ? '…' : '🗑'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper:      { borderTop: '1px solid #4a3d28', flexShrink: 0 },
  toggle:       { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer' },
  chevron:      { fontSize: 10, color: '#6a5d45' },
  body:         { padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  saveRow:      { display: 'flex', gap: 6 },
  nameInput:    { flex: 1, background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '7px 10px', fontSize: 13, outline: 'none' },
  saveBtn:      { background: '#3a1020', border: '1px solid #c0392b', borderRadius: 4, color: '#c0392b', fontSize: 12, fontWeight: 700, padding: '0 12px', cursor: 'pointer', whiteSpace: 'nowrap' },
  checkRow:     { display: 'flex', alignItems: 'center', cursor: 'pointer' },
  checkLabel:   { fontSize: 12, color: '#9a8b6e' },
  saveNote:     { fontSize: 11, color: '#4a3d28', lineHeight: 1.4 },
  hint:         { fontSize: 11, color: '#6a5d45', textAlign: 'center', padding: '4px 0' },
  entry:        { background: '#151525', border: '1px solid #4a3d28', borderRadius: 4, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 },
  entryInfo:    { display: 'flex', flexDirection: 'column', gap: 2 },
  entryName:    { fontSize: 13, fontWeight: 700, color: '#f0e6d0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  entryMeta:    { fontSize: 11, color: '#6a5d45' },
  entryActions: { display: 'flex', gap: 6 },
  loadBtn:      { flex: 1, height: 28, background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 6, color: '#c0392b', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  deleteBtn:    { width: 28, height: 28, background: 'transparent', border: 'none', color: '#6a5d45', fontSize: 13, cursor: 'pointer', borderRadius: 5 },
  confirmRow:   { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  confirmText:  { fontSize: 11, color: '#d4943a', flex: 1 },
  confirmYes:   { padding: '3px 10px', background: '#c0392b', border: 'none', borderRadius: 5, color: '#fff', fontSize: 11, fontWeight: 800, cursor: 'pointer' },
  confirmNo:    { padding: '3px 10px', background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 5, color: '#9a8b6e', fontSize: 11, cursor: 'pointer' },
};
